import {
  Toast,
  ToastBody,
  ToastTitle,
  Toaster,
  useId,
  useToastController,
} from '@fluentui/react-components';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';

type ToastTone = 'info' | 'success' | 'error';

interface ToastApi {
  toast: (title: string, opts?: { description?: string; tone?: ToastTone }) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}

const INTENT = { info: 'info', success: 'success', error: 'error' } as const;

export function ToastProvider({ children }: { children: ReactNode }) {
  const toasterId = useId('toaster');
  const { dispatchToast } = useToastController(toasterId);

  const toast = useCallback<ToastApi['toast']>(
    (title, opts) => {
      dispatchToast(
        <Toast>
          <ToastTitle>{title}</ToastTitle>
          {opts?.description && <ToastBody>{opts.description}</ToastBody>}
        </Toast>,
        { intent: INTENT[opts?.tone ?? 'info'], timeout: 6000 },
      );
    },
    [dispatchToast],
  );

  const api = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Toaster toasterId={toasterId} position="bottom-end" pauseOnHover />
    </ToastContext.Provider>
  );
}
